import {defineStore} from 'pinia'
import {IAdminClientsStore, TClientListItem} from "@app/types";
import {CustomersApi} from "@app/api/admin/customers-api";
import type { ICustomer } from '@torim-app/types';

const emptyCustomersList = (): IAdminClientsStore['customersList'] => ({
  items: [],
  total: 0,
  search: '',
  scrollTop: 0,
  extraPagesLoaded: 0,
  isLoaded: false
});

export const useAdminClientsStore = defineStore('admin-clients', {
  state: (): IAdminClientsStore => ({
    clients: [],
    totalCustomers: 0,
    customersList: emptyCustomersList()
  }),
  getters: {
    getClients(state): Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty' | 'smsOptedOut' | 'phoneNumber'>[] {
      return state.clients;
    },
    getTotalCustomers(state): number {
      return state.totalCustomers;
    },
    // Whether the customers list page still has customers left to append.
    hasMoreCustomersInList(state): boolean {
      return state.customersList.items.length < state.customersList.total;
    },
  },
  actions: {
    async fetchBusinessCustomers(args?: {
      limit?: number;
      skip?: number;
      search?: string;
    }) {
      try {
        const response = await CustomersApi.fetchCustomers(args);
        this.clients = response.customers;
        this.totalCustomers = response.total;
      } catch (e) {
        console.error(e)
        throw e;
      }
    },
    // First page of the customers list page — replaces whatever was cached.
    async fetchCustomersListFirstPage(limit: number, search?: string) {
      try {
        const response = await CustomersApi.fetchCustomers({ limit, skip: 0, search: search || undefined });
        this.customersList = {
          items: response.customers,
          total: response.total,
          search: search ?? '',
          scrollTop: 0,
          extraPagesLoaded: 0,
          isLoaded: true
        };
      } catch (e) {
        console.error(e)
        throw e;
      }
    },
    // Appends the next page. Customers are sorted by name, so ties across a page boundary
    // can repeat an item — dedupe by id rather than trusting the offset.
    async fetchCustomersListNextPage(limit: number) {
      const list = this.customersList;
      try {
        const response = await CustomersApi.fetchCustomers({
          limit,
          skip: list.items.length,
          search: list.search || undefined
        });
        const knownIds = new Set(list.items.map(customer => String(customer.id)));
        const newItems = response.customers.filter(customer => !knownIds.has(String(customer.id)));
        list.items = [...list.items, ...newItems];
        list.total = response.total;
        list.extraPagesLoaded += 1;
      } catch (e) {
        console.error(e)
        throw e;
      }
    },
    setCustomersListScrollTop(scrollTop: number) {
      this.customersList.scrollTop = scrollTop;
    },
    // Drops the cache so the customers list page refetches on its next visit. Call after
    // anything that adds / removes / renames a customer.
    resetCustomersList() {
      this.customersList = emptyCustomersList();
    },
    async searchCustomers(search: string, limit?: number, skip?: number): Promise<{
      customers: TClientListItem[];
      total: number;
    }> {
      try {
        return await CustomersApi.fetchCustomers({ search, limit, skip });
      } catch (e) {
        console.error(e)
        throw e;
      }
    },
  }
})
